import get from 'lodash/get';

import React from 'react';

import LogRocket from 'logrocket';

import Browser from './Browser';
import Payload from './Payload';

const Recording = {};

// Temporary hard gate: only enable LogRocket for specific device_uuid(s).
// This avoids broad sampling while we investigate production issues.
const LOGROCKET_ALLOWED_DEVICE_UUIDS = new Set([
  '27759571-0d39-4a82-8a2b-fd5394bfd411',
]);

const isLogRocketAllowedForDevice = (actionsPayload) => {
  try {
    const actions = get(actionsPayload, 'actions');
    const urls = Payload.findAdProperties(actions, 'url') || [];
    const clickUrls = Payload.findAdProperties(actions, 'click_through_url') || [];
    const trackingPixels = Payload.findAdProperties(actions, 'tracking_pixels') || [];

    const candidates = [
      // Most likely locations for device_uuid
      ...urls,
      ...clickUrls,
      ...trackingPixels,
      // As a fallback, check current page URL too
      get(window, 'location.href', ''),
    ].filter(Boolean);

    for (const u of candidates) {
      const deviceUuid = Browser.getParameterValue('device_uuid', String(u));
      const normalized = deviceUuid ? String(deviceUuid).toLowerCase() : '';
      if (normalized && LOGROCKET_ALLOWED_DEVICE_UUIDS.has(normalized)) {
        return true;
      }
    }
  } catch (e) {
    // no-op
  }
  return false;
};

/**
 * Should this set of actions get log rocket?
 * @param actionsPayload
 * @returns {boolean}
 */
Recording.shouldEnableLogRocket = actionsPayload => {
  return isLogRocketAllowedForDevice(actionsPayload);
};

/**
 * Enable log rocket using the payload provided
 * @param actionsPayload will be props in ContentView or serverPayload.data elsewhere.
 */
Recording.enableLogRocket = actionsPayload => {
  // Double-check gate so we never record unintended sessions.
  if (!isLogRocketAllowedForDevice(actionsPayload)) {
    return;
  }
  console.log('initializing logrocket!');
  LogRocket.init('ltzcc2/e2dev');
  console.log('logrocket actions payload: ', actionsPayload);
  // eslint-disable-next-line no-use-before-define
  const tv = Recording.getTrackingVars(actionsPayload);
  const { pubAppId, creativeId, campaignId, impressionId, bidId, adId, m2mRelsNo, m2mPelType } = tv;
  if (tv.imprId) {
    const userId = `impression_id:${tv.imprId}`;
    console.log(`for logrocket, identifying user as ${userId}`);
    LogRocket.identify(userId, {
      pubAppId,
      creativeId,
      campaignId,
      impressionId,
      bidId,
      adId,
      m2mRelsNo,
      m2mPelType,
    });
  }
  if (tv.pubUid) {
    const userId = `pub_uid:${tv.pubUid}`;
    console.log(`for logrocket, identifying user as ${userId}`);
    LogRocket.identify(userId, {
      pubAppId,
      creativeId,
      campaignId,
      impressionId,
      bidId,
      adId,
      m2mRelsNo,
      m2mPelType,
    });
  }
  // eslint-disable-next-line no-unused-vars
  LogRocket.getSessionURL(sessionURL => {
    window.server.logForImprIds('Recording_Enabled');
  });
};

Recording.getTrackingVars = actionsPayload => {
  const actions = get(actionsPayload, 'actions', []);
  const imprId = get(Payload.findAdProperties(actions, 'impression_id'), '[0]', '');
  const trackingPelUrl = get(Payload.findAdProperties(actions, 'tracking_pixels'), '[0]', '');
  const pubUid = Browser.getParameterValue('pub_uid', trackingPelUrl);
  let pubAppId = null;
  let creativeId = null;
  let campaignId = null;
  let impressionId = null;
  let bidId = null;
  let adId = null;
  let m2mRelsNo = null;
  let m2mPelType = null;
  try {
    const queueInterstitial = Payload.findAction(actions, 'queue_interstitial')[0];
    pubAppId = Payload.findAttrInAdArr(queueInterstitial.ads, 'publisher_app_id');
    creativeId = Payload.findAttrInAdArr(queueInterstitial.ads, 'creative_id');
    campaignId = Payload.findAttrInAdArr(queueInterstitial.ads, 'campaign_id');
    impressionId = Payload.findAttrInAdArr(queueInterstitial.ads, 'impression_id');
    bidId = Payload.findAttrInAdArr(queueInterstitial.ads, 'bid_id');
    adId = Payload.findAttrInAdArr(queueInterstitial.ads, 'ad_id');
    m2mRelsNo = Payload.findAttrInAdArr(queueInterstitial.ads, 'm2m_relsno');
    m2mPelType = Payload.findAttrInAdArr(queueInterstitial.ads, 'm2m_pel_type');
  } catch (e) {
    console.warn('Error finding attr in ad action: ', e);
  }
  return {
    pubUid,
    imprId,
    pubAppId,
    creativeId,
    campaignId,
    impressionId,
    bidId,
    adId,
    m2mRelsNo,
    m2mPelType,
  };
};

export default Recording;
